/**
 * "Your semester" (#495): a light timeline of the checked answers behind each topic, one row per topic, one column per week.
 *
 * A mark is one observation, distinguished by SHAPE (a filled circle for homework right, a cross for homework wrong, a diamond
 * for on your own in the app, an open circle for an answer under review) and never by colour alone; all marks take the focused
 * course's `--accent`. There is no density bar, no count and no percentage: the weeks are ordinals, and a week with two marks
 * shows two marks.
 *
 * The timeline is split across the topic cards (#556): the key (legend and week numbers) sits once above them, and each card
 * holds its own row of marks in the same columns. For a screen reader each mark carries the same sentence as the evidence
 * trail as its label; the week header and the shapes themselves are decorative. Hebrew runs right to left (the grid follows the
 * document direction); dates stay left to right.
 */

import type { CourseTopicsResponse } from '@davinci/contracts';
import { useLocale, useMessages } from '../../i18n/locale.js';
import { defineMessages } from '../../i18n/messages.js';
import { entryLabel, entryShape, type EvidenceEntry } from './evidence-entries.js';

type Topic = CourseTopicsResponse['topics'][number];

const WEEK_MS = 7 * 24 * 3_600_000;

const messages = defineMessages({
  en: {
    heading: 'Your semester',
    week: 'Week',
    homeworkRight: 'homework ✓',
    homeworkWrong: 'homework ✗',
    inApp: 'on your own, in the app',
    review: 'under review',
    rowsLabel: 'Timeline of evidence per topic across the semester weeks',
    empty: 'Checked answers will appear here as weeks go by.',
  },
  he: {
    heading: 'הסמסטר שלכם',
    week: 'שבוע',
    homeworkRight: 'שיעורי בית ✓',
    homeworkWrong: 'שיעורי בית ✗',
    inApp: 'לבד, באפליקציה',
    review: 'בבדיקה',
    rowsLabel: 'ציר זמן של הראיות לכל נושא לאורך שבועות הסמסטר',
    empty: 'התשובות שנבדקו יופיעו כאן עם חלוף השבועות.',
  },
});

/** The start of the week (Sunday, UTC) holding this instant. Only used to put marks in columns. */
function weekStart(ms: number): number {
  const day = new Date(ms);
  const sunday = Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate()) - day.getUTCDay() * 24 * 3_600_000;
  return sunday;
}

export interface SemesterSpan {
  readonly first: number;
  readonly weeks: number;
}

/** The weeks the marks span, or null when no topic has a checked answer yet. */
export function semesterSpan(topics: readonly Topic[], now: number = Date.now()): SemesterSpan | null {
  const all = topics.flatMap((topic) => topic.evidence);
  if (all.length === 0) return null;
  const first = weekStart(Math.min(...all.map((e) => Date.parse(e.uploadedAt))));
  const last = weekStart(Math.max(now, ...all.map((e) => Date.parse(e.uploadedAt))));
  return { first, weeks: Math.round((last - first) / WEEK_MS) + 1 };
}

const columnsOf = (span: SemesterSpan): React.CSSProperties => ({ gridTemplateColumns: `repeat(${span.weeks}, minmax(0, 1fr))` });

/**
 * "Your semester" as one card: the legend at the top end, the week numbers, then one row per topic (name, then its marks).
 * Every topic has a row, marked or not. On a phone the name sits above its marks (progress.css).
 */
export function SemesterCard(props: { readonly topics: readonly Topic[]; readonly span: SemesterSpan | null }): React.JSX.Element {
  const t = useMessages(messages);
  const { span, topics } = props;
  return (
    <section className="semester" aria-labelledby="semester-heading">
      <div className="semester-top">
        <h2 id="semester-heading">{t.heading}</h2>
        {span === null ? null : (
          <p className="semester-legend">
            <span>
              <span aria-hidden="true" className="mark-right">● </span>
              {t.homeworkRight}
            </span>
            <span>
              <span aria-hidden="true" className="mark-wrong">✕ </span>
              {t.homeworkWrong}
            </span>
            <span>
              <span aria-hidden="true" className="mark-own">◆ </span>
              {t.inApp}
            </span>
            <span>
              <span aria-hidden="true" className="mark-review">○ </span>
              {t.review}
            </span>
          </p>
        )}
      </div>
      {span === null ? (
        <p className="semester-empty">{t.empty}</p>
      ) : (
        <div className="semester-rows" role="group" aria-label={t.rowsLabel}>
          <div className="semester-row semester-row-head" aria-hidden="true">
            <span />
            <div className="semester-weeks" style={columnsOf(span)}>
              {Array.from({ length: span.weeks }, (_, i) => (
                <span key={i} className="semester-week" dir="ltr">
                  {i + 1}
                </span>
              ))}
            </div>
          </div>
          {topics.map((topic) => (
            <TopicRow key={topic.topicKey} topic={topic} span={span} />
          ))}
        </div>
      )}
    </section>
  );
}

function TopicRow(props: { readonly topic: Topic; readonly span: SemesterSpan }): React.JSX.Element {
  const { locale } = useLocale();
  const { topic, span } = props;
  const byWeek = new Map<number, EvidenceEntry[]>();
  for (const e of topic.evidence) {
    const week = Math.round((weekStart(Date.parse(e.uploadedAt)) - span.first) / WEEK_MS);
    byWeek.set(week, [...(byWeek.get(week) ?? []), e]);
  }
  return (
    <div className="semester-row">
      <span className="semester-name">
        <bdi dir="auto">{topicTitle(topic, locale)}</bdi>
      </span>
      <div className="semester-marks" style={columnsOf(span)}>
        {[...byWeek.entries()].map(([week, entries]) => (
          <span key={week} className="semester-cell" style={{ gridColumn: week + 1, gridRow: 1 }}>
            {entries.map((e) => {
              const shape = entryShape(e);
              return (
                <span key={e.claimId} role="img" className={`semester-mark ${MARK_CLASS[shape]}`} aria-label={entryLabel(e, locale)}>
                  <span aria-hidden="true">{shape}</span>
                </span>
              );
            })}
          </span>
        ))}
      </div>
    </div>
  );
}

const MARK_CLASS = { '●': 'mark-right', '✕': 'mark-wrong', '◆': 'mark-own', '○': 'mark-review' } as const;

/** The topic's display name: its section name (imported course text, rendered as text only) or "Section n". */
export function topicTitle(topic: Topic, locale: 'en' | 'he'): string {
  return topic.sectionName ?? (locale === 'he' ? `פרק ${topic.sectionNumber}` : `Section ${topic.sectionNumber}`);
}
